import type { Control, Resolver } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button2 } from "@/components/UI2/atoms/Button";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import { CredentialsMultiSelect } from "@/portals/(shared)/components/CredentialsMultiSelect";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { isUserAdmin, isUserPCP } from "@/utils/authToken";
import { PhoneNumberTextField } from "@/features/form/PhoneNumberTextField";
import type { ProviderProfile, ProviderType, UUID } from "@/core/types";
import { showToast } from "@/providers/ToastProvider";
import { TextField2 } from "@/components/UI2/molecules/TextField";
import { useProviderProfile } from "@/portals/(provider)/(profile)/api/queries/useProviderProfile";
import { useUpdateProviderProfile } from "@/portals/(provider)/(profile)/api/mutations/useUpdateProviderProfile";

import { getBasicInfoSchema, type BasicInfoFormValues } from "../schema";

import { ProviderProfileFormSkeleton } from "./ProviderProfileFormSkeleton";

export type ExtraFieldRenderProps = {
  control: Control<BasicInfoFormValues>;
  isReadOnly: boolean;
  providerType: Nullable<ProviderType>;
};

type ProviderProfileFormProps = {
  providerId: Nullable<UUID>;
  renderExtraField?: (props: ExtraFieldRenderProps) => ReactNode;
  title: string;
};

export function ProviderProfileForm({
  providerId,
  renderExtraField,
  title,
}: ProviderProfileFormProps) {
  const [isEditing, setIsEditing] = useState(false);

  const { data: profileData, isLoading: isProfileLoading } = useProviderProfile(providerId);
  const { isPending, mutate } = useUpdateProviderProfile(providerId, {
    onError: (error) => {
      captureErrorMessage(parseError({ error, fallback: "Failed to update provider profile" }), {
        tags: { component: "ProviderProfileForm", errorType: "submit_profile" },
      });
      showToast([{ heading: "Failed to update profile.", variant: "error" }]);
    },
    onSuccess: () => setIsEditing(false),
  });

  const isAdmin = isUserAdmin();
  const isPCP = isUserPCP();

  const { control, formState, handleSubmit, reset } = useForm<BasicInfoFormValues>({
    defaultValues: {
      cp_credentials: [],
      email: "",
      intake_practice_ids: [],
      mobile: "",
      name: "",
      npi_number: "",
      practice: "",
      provider_type: "",
    },
    mode: "onChange",
    resolver: zodResolver(getBasicInfoSchema(isAdmin)) as Resolver<BasicInfoFormValues>,
  });

  useEffect(() => {
    if (profileData) reset(mapToFormValues(profileData));
  }, [profileData, reset]);

  const handleCancel = useCallback(() => {
    if (profileData) reset(mapToFormValues(profileData));
    setIsEditing(false);
  }, [profileData, reset]);

  const onSubmit = (values: BasicInfoFormValues) => {
    mutate({
      body: {
        mobile: values.mobile,
        ...(isAdmin && {
          cp_credentials: values.cp_credentials.join(", "),
          name: values.name,
          npi_number: values.npi_number,
        }),
        ...(renderExtraField && {
          intake_practice_ids: (values.intake_practice_ids as UUID[]) ?? [],
        }),
      },
      providerId: providerId ?? undefined,
    });
  };

  if (isProfileLoading) {
    return <ProviderProfileFormSkeleton title={title} />;
  }

  return (
    <DashboardCard2
      Actions={
        !isEditing && (
          <Button2
            onPress={() => setIsEditing(true)}
            variant="link"
            {...getAutomationDataAttr("ProviderProfileBasicInfoEditButton")}
          >
            Edit
          </Button2>
        )
      }
      title={title}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="tw:grid tw:gap-5 tw:md:grid-cols-2">
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState }) => (
              <TextField2
                errorMessage={fieldState.error?.message}
                hasStrongLabel
                isInvalid={!!fieldState.error}
                isReadOnly={!isEditing || !isAdmin}
                isRequired={isEditing && isAdmin}
                label="Full Name"
                mode="input"
                onChange={field.onChange}
                value={field.value}
                {...getAutomationDataAttr("ProviderBasicInfoFullName")}
              />
            )}
          />
          <Controller
            control={control}
            name="provider_type"
            render={({ field }) => (
              <TextField2
                hasStrongLabel
                isReadOnly
                label="Provider Type"
                mode="input"
                onChange={field.onChange}
                value={field.value}
                {...getAutomationDataAttr("ProviderBasicInfoProviderType")}
              />
            )}
          />
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <TextField2
                hasStrongLabel
                isReadOnly
                label="Email"
                mode="input"
                onChange={field.onChange}
                value={field.value}
                {...getAutomationDataAttr("ProviderBasicInfoEmail")}
              />
            )}
          />
          <Controller
            control={control}
            name="mobile"
            render={({ field, fieldState }) => (
              <PhoneNumberTextField
                errorMessage={fieldState.error?.message}
                hasStrongLabel
                isInvalid={!!fieldState.error}
                isReadOnly={!isEditing}
                isRequired={isEditing}
                label="Mobile"
                phoneNumberInputProps={{ onChange: field.onChange, value: field.value }}
                {...getAutomationDataAttr("ProviderBasicInfoMobile")}
              />
            )}
          />
          <CredentialsMultiSelect
            control={control}
            isReadOnly={!isEditing || !isAdmin}
            providerType={profileData?.provider_type}
          />
          <Controller
            control={control}
            name="npi_number"
            render={({ field, fieldState }) => (
              <TextField2
                errorMessage={fieldState.error?.message}
                hasStrongLabel
                isInvalid={!!fieldState.error}
                isReadOnly={!isEditing || !isAdmin}
                isRequired={isEditing && isAdmin && !isPCP}
                label="NPI Number"
                mode="input"
                onChange={field.onChange}
                value={field.value}
                {...getAutomationDataAttr("ProviderBasicInfoNPINumber")}
              />
            )}
          />
          <Controller
            control={control}
            name="practice"
            render={({ field }) => (
              <TextField2
                hasStrongLabel
                isReadOnly
                label="Practice"
                mode="input"
                onChange={field.onChange}
                value={field.value}
                {...getAutomationDataAttr("ProviderBasicInfoPractice")}
              />
            )}
          />
          {renderExtraField?.({
            control,
            isReadOnly: !isEditing,
            providerType: profileData?.provider_type,
          })}
        </div>

        {!isAdmin && isEditing && (
          <p className="ProviderProfile__toUpdateMessage">
            To update read-only fields, contact{" "}
            <a href="mailto:support@aptihealth.com">support@aptihealth.com</a>.
          </p>
        )}

        {isEditing && (
          <div className="tw:mt-5 tw:flex tw:justify-end tw:gap-3">
            <Button2 onPress={handleCancel} size="md" type="button" variant="secondary">
              Cancel
            </Button2>

            <Button2
              isDisabled={!formState.isValid || isPending}
              isLoading={isPending}
              labelLoading={null}
              size="md"
              type="submit"
              variant="primary"
            >
              Save
            </Button2>
          </div>
        )}
      </form>
    </DashboardCard2>
  );
}

function mapToFormValues(data: ProviderProfile): BasicInfoFormValues {
  return {
    cp_credentials: data.cp_credentials
      ? data.cp_credentials
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [],
    email: data.email ?? "",
    intake_practice_ids: data.intake_practice_ids ?? [],
    mobile: data.mobile ?? "",
    name: data.name ?? "",
    npi_number: data.npi_number ?? "",
    practice: data.practice ?? "",
    provider_type: data.provider_type ?? "",
  };
}
